import { useQuery } from "@tanstack/react-query";

import { UserRolePermissions } from "@pdpp/lib-planta";

import { plantaApi } from "../../api/fetch";
import { OPP_AUTHZ } from "../../utils/runtimeConfig";
import { userPermissions$ } from "./state";

const hasPermissions = (
  userPerms: UserRolePermissions | null | undefined,
): boolean =>
  !!userPerms &&
  Array.isArray(userPerms.roles) &&
  userPerms.roles.length > 0 &&
  Array.isArray(userPerms.permissions) &&
  userPerms.permissions.length > 0;

export function setImpersonatedUserPermissions(
  impersonatedPermissions: UserRolePermissions | null | undefined,
): void {
  if (impersonatedPermissions && hasPermissions(impersonatedPermissions)) {
    userPermissions$.value = impersonatedPermissions;
    return;
  }
}

export const useGetUserPermissions = (): {
  data: UserRolePermissions | null;
  refetch: () => void;
  isLoading: boolean;
  isError: boolean;
} => {
  const cached = userPermissions$.value;
  const hasImpersonatedPermissions = hasPermissions(cached);

  const enabled = OPP_AUTHZ && !hasImpersonatedPermissions;

  const {
    data,
    refetch,
    isLoading: loading,
    isError,
  } = useQuery<UserRolePermissions | null>({
    queryKey: ["opp-user-permissions"],
    enabled,
    retry: false,
    initialData: hasImpersonatedPermissions ? cached : null,
    staleTime: hasImpersonatedPermissions ? Infinity : 0,
    queryFn: async () => {
      const res =
        await plantaApi.jsonInJsonOut<UserRolePermissions>(
          "/users/permissions",
        );
      userPermissions$.value = res;
      return res;
    },
  });

  return {
    data: data ?? null,
    refetch: () => {
      void refetch();
    },
    isLoading: enabled ? loading : false,
    isError,
  };
};
